/** Parseo CSV RFC4180 simplificado (coma o punto y coma, comillas, saltos de línea). */

export type ParsedCsv = {
  headers: string[];
  rows: string[][];
  delimiter: ',' | ';';
};

const normalizeHeader = (value: string) =>
  value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase();

/** Excel en español suele exportar CSV con `;` en lugar de `,`. */
export function detectCsvDelimiter(headerLine: string): ',' | ';' {
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;

  for (let i = 0; i < headerLine.length; i += 1) {
    const ch = headerLine[i];
    if (ch === '"') {
      if (inQuotes && headerLine[i + 1] === '"') {
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes) {
      if (ch === ',') commas += 1;
      if (ch === ';') semicolons += 1;
    }
  }

  return semicolons > commas ? ';' : ',';
}

function parseRow(line: string, delimiter: ',' | ';'): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells.map((c) => c.replace(/^\t+/, '').trim());
}

function splitLogicalLines(text: string): string[] {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && text[i + 1] === '\n') {
        i += 1;
      }
      if (current.trim() !== '') {
        lines.push(current);
      }
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim() !== '') {
    lines.push(current);
  }
  return lines;
}

export function parseCsvText(text: string): ParsedCsv {
  const normalized = text.replace(/^\uFEFF/, '').trim();
  if (!normalized) {
    return { headers: [], rows: [], delimiter: ',' };
  }

  const logical = splitLogicalLines(normalized);
  if (logical.length === 0) {
    return { headers: [], rows: [], delimiter: ',' };
  }

  const delimiter = detectCsvDelimiter(logical[0]);
  const headers = parseRow(logical[0], delimiter).map(normalizeHeader);
  const rows = logical.slice(1).map((line) => parseRow(line, delimiter));
  return { headers, rows, delimiter };
}

export function rowToRecord(headers: string[], cells: string[]): Record<string, string> {
  const record: Record<string, string> = {};
  for (let i = 0; i < headers.length; i += 1) {
    const key = headers[i];
    if (!key) continue;
    record[key] = cells[i] ?? '';
  }
  return record;
}
